import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { Mail, MapPin } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout.jsx';
import CRUDTable from '../../components/crud/CRUDTable';
import CRUDModal from '../../components/crud/CRUDModal';
import DeleteConfirmModal from '../../components/crud/DeleteConfirmModal';

// GraphQL Queries y Mutations
const GET_ORIGINS = gql`
  query GetOrigins {
    origins {
      id
      origin_name
      email
      shipments {
        manifest
      }
    }
  }
`;

const CREATE_ORIGIN = gql`
  mutation CreateOrigin($origin_name: String!, $email: String) {
    createOrigin(origin_name: $origin_name, email: $email) {
      id
      origin_name
      email
      shipments {
        manifest
      }
    }
  }
`;

const UPDATE_ORIGIN = gql`
  mutation UpdateOrigin($id: UUID!, $origin_name: String!, $email: String) {
    updateOrigin(id: $id, origin_name: $origin_name, email: $email) {
      id
      origin_name
      email
      shipments {
        manifest
      }
    }
  }
`;

const DELETE_ORIGIN = gql`
  mutation DeleteOrigin($id: UUID!) {
    deleteOrigin(id: $id)
  }
`;

const OriginsPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedOrigin, setSelectedOrigin] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Queries
  const { data, loading, error } = useQuery(GET_ORIGINS);

  // Mutations
  const [createOrigin, { loading: createLoading }] = useMutation(CREATE_ORIGIN, {
    onCompleted: () => {
      toast.success('Origen creado exitosamente');
      setIsModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
    refetchQueries: [{ query: GET_ORIGINS }]
  });

  const [updateOrigin, { loading: updateLoading }] = useMutation(UPDATE_ORIGIN, {
    onCompleted: () => {
      toast.success('Origen actualizado exitosamente');
      setIsModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
    refetchQueries: [{ query: GET_ORIGINS }]
  });

  const [deleteOrigin, { loading: deleteLoading }] = useMutation(DELETE_ORIGIN, {
    onCompleted: () => {
      toast.success('Origen eliminado exitosamente');
      setIsDeleteModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
    refetchQueries: [{ query: GET_ORIGINS }]
  });

  // Configuración de columnas para la tabla
  const columns = [
    {
      key: 'origin_name',
      label: 'Nombre del Origen',
      sortable: true,
      render: (name) => (
        <div className="flex items-center">
          <MapPin className="w-4 h-4 text-gray-500 mr-2" />
          <span className="font-medium">{name}</span>
        </div>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      type: 'email',
      render: (email) => (
        email ? (
          <div className="flex items-center">
            <Mail className="w-4 h-4 text-gray-500 mr-2" />
            <a href={`mailto:${email}`} className="text-blue-600 hover:text-blue-800">
              {email}
            </a>
          </div>
        ) : (
          <span className="text-gray-500">Sin email</span>
        )
      ),
    },
    {
      key: 'shipments',
      label: 'Envíos',
      render: (shipments) => (
        <div className="flex items-center">
          <span className={`px-2 py-1 text-xs rounded-full ${
            shipments.length > 0 
              ? 'bg-blue-100 text-blue-800' 
              : 'bg-gray-100 text-gray-600'
          }`}>
            {shipments.length} envío{shipments.length !== 1 ? 's' : ''}
          </span>
        </div>
      ),
    },
  ];

  // Configuración de campos para el modal
  const formFields = [
    {
      name: 'origin_name',
      label: 'Nombre del Origen',
      type: 'text',
      required: true,
      placeholder: 'Ingrese el nombre del origen',
      minLength: 2,
      maxLength: 100,
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: false,
      placeholder: 'Ingrese el email (opcional)',
      maxLength: 255,
    },
  ];

  // Handlers
  const handleAdd = () => {
    setSelectedOrigin(null);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleEdit = (origin) => {
    setSelectedOrigin(origin);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDelete = (origin) => {
    setSelectedOrigin(origin);
    setIsDeleteModalOpen(true);
  };

  const handleSave = async (formData) => {
    try {
      if (isEditing && selectedOrigin) {
        await updateOrigin({
          variables: {
            id: selectedOrigin.id,
            origin_name: formData.origin_name,
            email: formData.email || null,
          },
        });
      } else {
        await createOrigin({
          variables: {
            origin_name: formData.origin_name,
            email: formData.email || null,
          },
        });
      }
    } catch (error) {
      // Error handled by mutation onError
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteOrigin({
        variables: {
          id: selectedOrigin.id,
        },
      });
    } catch (error) {
      // Error handled by mutation onError
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrigin(null);
    setIsEditing(false);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedOrigin(null);
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error al cargar los orígenes: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Gestión de Orígenes
          </h1>
          <p className="text-gray-600">
            Administra los orígenes disponibles para los envíos del sistema
          </p>
        </div>

        <CRUDTable
          data={data?.origins || []}
          columns={columns}
          isLoading={loading}
          searchPlaceholder="Buscar orígenes..."
          searchFields={['origin_name', 'email']}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          addButtonText="Agregar Origen"
          emptyMessage="No hay orígenes disponibles"
        />

        <CRUDModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSave}
          title={isEditing ? 'Editar Origen' : 'Agregar Origen'}
          initialData={selectedOrigin || {}}
          fields={formFields}
          isLoading={createLoading || updateLoading}
          isEditing={isEditing}
        />

        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          title="Eliminar Origen"
          message="¿Estás seguro de que deseas eliminar este origen?"
          itemName={selectedOrigin?.origin_name}
          isLoading={deleteLoading}
          isDangerous={selectedOrigin?.shipments?.length > 0}
        />
      </div>
    </DashboardLayout>
  );
};

export default OriginsPage;